// Bid negotiation thread modal: chat + counter offers, pre-assignment.
import React from "react";
import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { DollarSign, Send, X } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBidSocket } from "@/hooks/useBidSocket";
import { notifyThreadsChanged } from "@/hooks/useUnreadThreads";

// ---------- Bid thread modal: pre-assignment negotiation ----------
export function BidThreadModal({ bid, onClose, onChange }) {
  const { user } = useAuth();
  const { messages, connected, typingUsers, sendMessage, sendTyping } = useBidSocket(bid.id);
  const [req, setReq] = useState(null);
  const [text, setText] = useState("");
  const [proposing, setProposing] = useState(false);
  const [price, setPrice] = useState("");
  const [note, setNote] = useState("");
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get(`/requests/${bid.request_id}`).then((r) => setReq(r.data)).catch(() => {});
  }, [bid.request_id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  // Opening/viewing the thread marks it read server-side (drives unread badges).
  useEffect(() => {
    api.get(`/bids/${bid.id}/messages`).then(() => notifyThreadsChanged()).catch(() => {});
  }, [bid.id, messages.length]);

  const threadOpen = req?.status === "open";
  const counterparty = user?.id === bid.driver_id ? "shipper" : "driver";

  const sendText = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (!sendMessage(text.trim())) toast.error("Connection lost — retrying…");
    setText("");
    sendTyping(false);
  };

  const sendCounter = async () => {
    const p = parseInt(price, 10);
    if (!p || p < 1) {
      toast.error("Enter a price.");
      return;
    }
    try {
      await api.post(`/bids/${bid.id}/counter`, { price_usd: p, note: note.trim() });
      setProposing(false);
      setPrice("");
      setNote("");
      onChange?.();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const respond = async (msgId, accept) => {
    try {
      await api.post(`/messages/${msgId}/counter/respond`, null, { params: { accept } });
      toast.success(accept ? "Accepted — job assigned at that price." : "Counter declined.");
      onChange?.();
      if (accept) onClose();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const COUNTER_STATUS = {
    pending: "text-amber-400 border-amber-500/50",
    accepted: "text-emerald-400 border-emerald-500/50",
    declined: "text-zinc-500 border-white/10",
    superseded: "text-zinc-600 border-white/[0.06] line-through",
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-4" onClick={onClose} data-testid="bid-thread-modal">
      <div className="bg-[#0F0F10] border border-white/[0.08] w-full max-w-lg max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-white/[0.08]">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-amber-400 font-bold">Negotiation thread</div>
            <div className="font-semibold text-white truncate mt-0.5" data-testid="bid-thread-title">
              ${bid.price_usd.toLocaleString()} bid · {bid.driver_name}
            </div>
          </div>
          <button onClick={onClose} data-testid="bid-thread-close" aria-label="Close" className="text-zinc-500 hover:text-white shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-48" data-testid="bid-thread-messages">
          {messages.length === 0 && (
            <p className="text-xs text-zinc-600 text-center py-8">
              No messages yet. Talk it through, then propose a price with the $ button.
            </p>
          )}
          {messages.map((m) => {
            const mine = m.sender_id === user?.id;
            if (m.kind === "counter") {
              const live = m.counter_status === "pending" && !mine && threadOpen;
              return (
                <div key={m.id} className={`border p-3 ${mine ? "ml-8 border-white/[0.08]" : "mr-8 border-amber-500/40 bg-amber-500/[0.05]"}`} data-testid={`counter-bubble-${m.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="font-display text-xl font-black">${(m.price_usd || 0).toLocaleString()}</span>
                    <span className={`text-[9px] uppercase tracking-widest font-black border px-1.5 py-0.5 ${COUNTER_STATUS[m.counter_status] || COUNTER_STATUS.pending}`}>
                      {m.counter_status}
                    </span>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mt-0.5">
                    proposed by {mine ? "you" : m.sender_name}
                  </div>
                  {m.text && <div className="text-xs text-zinc-300 mt-1.5">{m.text}</div>}
                  {live && (
                    <div className="mt-3 flex items-center gap-2">
                      <button
                        onClick={() => respond(m.id, true)}
                        data-testid={`thread-counter-accept-${m.id}`}
                        className="text-[10px] uppercase tracking-widest font-bold bg-emerald-500 hover:bg-emerald-400 text-black px-3 py-1.5 transition-colors"
                      >
                        Accept — assign at ${(m.price_usd || 0).toLocaleString()}
                      </button>
                      <button
                        onClick={() => respond(m.id, false)}
                        data-testid={`thread-counter-decline-${m.id}`}
                        className="text-[10px] uppercase tracking-widest font-bold text-zinc-400 hover:text-white border border-white/10 hover:border-white px-3 py-1.5 transition-colors"
                      >
                        Decline
                      </button>
                    </div>
                  )}
                  {m.counter_status === "pending" && mine && (
                    <div className="text-[10px] text-zinc-600 mt-2 uppercase tracking-widest">Waiting for the {counterparty}…</div>
                  )}
                </div>
              );
            }
            return (
              <div key={m.id} className={`max-w-[85%] ${mine ? "ml-auto text-right" : ""}`} data-testid={`thread-msg-${m.id}`}>
                <div className={`inline-block px-3 py-2 text-sm ${mine ? "bg-[#FF3B30] text-white" : "bg-[#1A1A1D] text-zinc-200"}`}>
                  {m.text}
                </div>
                {m.flagged && <div className="text-[9px] text-amber-500 uppercase tracking-widest mt-0.5">moderated: {m.flag_reason}</div>}
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {Object.keys(typingUsers).length > 0 && (
          <div className="px-4 pb-1 text-[10px] text-zinc-500 uppercase tracking-widest">
            {Object.values(typingUsers).join(", ")} typing…
          </div>
        )}

        <div className="p-3 border-t border-white/[0.08] space-y-2">
          {proposing && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="thread-propose-form">
              <input
                type="number"
                min="1"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="Price $"
                data-testid="thread-counter-price"
                className="w-28 bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white p-2 text-sm"
              />
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Note (optional)"
                data-testid="thread-counter-note"
                maxLength={500}
                className="flex-1 min-w-32 bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-2 text-sm"
              />
              <button
                onClick={sendCounter}
                data-testid="thread-counter-send"
                className="text-[10px] uppercase tracking-widest font-bold bg-amber-500 hover:bg-amber-400 text-black px-3 py-2 transition-colors"
              >
                Propose
              </button>
              <button onClick={() => setProposing(false)} className="text-zinc-500 hover:text-white px-1" aria-label="Cancel proposal">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {threadOpen ? (
            <form onSubmit={sendText} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setProposing((p) => !p)}
                data-testid="thread-propose-toggle"
                title="Propose a price"
                className={`shrink-0 border p-2.5 transition-colors ${proposing ? "border-amber-500 text-amber-400" : "border-white/10 text-zinc-400 hover:text-amber-400 hover:border-amber-500/60"}`}
              >
                <DollarSign className="w-4 h-4" />
              </button>
              <input
                value={text}
                onChange={(e) => { setText(e.target.value); sendTyping(true); }}
                placeholder={connected ? `Message the ${counterparty}…` : "Connecting…"}
                data-testid="thread-message-input"
                maxLength={2000}
                className="flex-1 bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-2.5 text-sm"
              />
              <button type="submit" data-testid="thread-send" className="shrink-0 bg-[#FF3B30] hover:bg-[#D62828] text-white p-2.5 transition-colors" aria-label="Send">
                <Send className="w-4 h-4" />
              </button>
            </form>
          ) : (
            <p className="text-[11px] text-zinc-500 uppercase tracking-widest text-center py-1" data-testid="thread-closed-note">
              Thread closed — continue in the job chat on the request page
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

